const LAST_SEGMENT_MAX = 12;

export type BreadcrumbCollapseLevel = 0 | 1 | 2;

export interface BreadcrumbLabelInput {
  label: string;
  index: number;
  total: number;
  /** When true, shorten the first segment (pillar) to one letter + "." */
  compactFirstSegment?: boolean;
  /** When true, truncate the last segment to 12 chars + ellipsis */
  truncateLastSegment?: boolean;
}

export interface FormattedBreadcrumbLabel {
  display: string;
  full: string;
}

export interface BreadcrumbCollapseFlags {
  compactFirstSegment: boolean;
  truncateLastSegment: boolean;
}

export function resolveBreadcrumbCollapseFlags(
  level: BreadcrumbCollapseLevel,
  total: number
): BreadcrumbCollapseFlags {
  return {
    compactFirstSegment: level >= 1 && total >= 3,
    truncateLastSegment: level >= 2,
  };
}

function truncateLast(label: string): string {
  if (label.length <= LAST_SEGMENT_MAX) return label;
  return `${label.slice(0, LAST_SEGMENT_MAX)}…`;
}

function shortenFirst(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return label;
  return `${trimmed.charAt(0)}.`;
}

/**
 * Formats breadcrumb labels for single-line header display.
 * Progressive collapse (driven by container width in HeaderBreadcrumbs):
 * - Level 0: all segments full
 * - Level 1: first segment (pillar) → one letter + "." when 3+ crumbs
 * - Level 2: also truncate last segment to 12 chars + ellipsis
 * Middle segments are unchanged at every level.
 */
export function formatBreadcrumbLabel({
  label,
  index,
  total,
  compactFirstSegment = false,
  truncateLastSegment = false,
}: BreadcrumbLabelInput): FormattedBreadcrumbLabel {
  const full = label;
  const isFirst = index === 0;
  const isLast = index === total - 1;

  if (compactFirstSegment && isFirst && total >= 3) {
    return { display: shortenFirst(label), full };
  }
  if (truncateLastSegment && isLast && total > 0) {
    return { display: truncateLast(label), full };
  }
  return { display: label, full };
}

export interface FormatBreadcrumbLabelsOptions {
  collapseLevel?: BreadcrumbCollapseLevel;
}

export function formatBreadcrumbLabels(
  labels: string[],
  options: FormatBreadcrumbLabelsOptions = {}
): FormattedBreadcrumbLabel[] {
  const { collapseLevel = 0 } = options;
  const flags = resolveBreadcrumbCollapseFlags(collapseLevel, labels.length);
  return labels.map((label, index) =>
    formatBreadcrumbLabel({ label, index, total: labels.length, ...flags })
  );
}
